import { loadRecaptcha } from '@partials/UI/form/modules/recaptcha/recaptchaLoader';

export default class Modal {
    /**
     *
     * @param {Object}
     * @property {string} selector - ID модального окна
     * @property {string} close - Селектор элементов закрытия
     * @property {string} open - Селектор элементов открытия
     * @property {string} overlay - Селектор overlay
     * @property {boolean} hasVideo - Налачие видео в modal
     */
    constructor({ selector, close, open = null, overlay, hasVideo = false, hasForm = false }) {
        this.$selector = document.querySelector(selector);
        this.$close = document.querySelectorAll(close);
        this.$open = document.querySelectorAll(open) || null;
        this.$overlay = document.querySelector(overlay);
        this.hasVideo = hasVideo;
        this.hasForm = hasForm;

        this.isOpen = false; // Флаг, чтобы отслеживать, открыто ли окно
        this.videoElements = []; // Кешированные видео элементы

        document.documentElement.addEventListener("keydown", this.handleEscape.bind(this));
        this.setup();
    }

    setup() {
        this.$open && this.eventHandler("click", this.$open, this.open.bind(this));
        this.$overlay && this.eventHandler("click", this.$overlay, this.close.bind(this));
        this.$close && this.eventHandler("click", this.$close, this.close.bind(this));
    }

    open() {
        this.isOpen = true;
        this.handler("open", "active", this.$selector);
        this.handler("open", "active", this.$overlay);
        // this.handler("open", "no-scroll", document.body);

        if (this.hasVideo) {
            // Ленивая загрузка и автовоспроизведение HTML5 video при открытии
            const videos = this.$selector.querySelectorAll("video");
            videos.forEach(video => this.handleVideoLazyLoad(video));
        }

        if (this.hasForm) {
            loadRecaptcha()
        }
    }

    async close() {
        this.isOpen = false;
        this.handler("close", "active", this.$selector);
        this.handler("close", "active", this.$overlay);
        // this.handler("close", "no-scroll", document.body);

        if (this.hasVideo) {
            // Обработка iframe видео
            const frame = this.$selector.querySelector("iframe");
            if (frame) frame.src = "";

            // Обработка HTML5 video элементов
            this.videoElements.forEach(video => {
                if (!video.paused) {
                    video.pause();
                }
                // НЕ сбрасываем время к началу, чтобы сохранить позицию при повторном открытии
            });
        }

        // if (this.hasForm) {
        //     this.clearModalForms();
        // }
    }

    handler(action, className, elements) {
        if (elements) {
            elements.classList.toggle(className, action === "open");
        }
    }

    handleEscape(e) {
        if (e.key === "Escape" && this.isOpen) {
            this.close();
        }
    }

    eventHandler(event, elements, callback) {
        // Проверяем, что переданы элементы и есть что обрабатывать
        if (!elements) return;

        // Проверяем, поддерживает ли объект forEach (NodeList или массив)
        if (elements.forEach) {
            elements.forEach(el => el?.addEventListener && el.addEventListener(event, callback));
        } else if (elements.addEventListener) {
            elements.addEventListener(event, callback);
        }
    }

    /**
     * Обработка ленивой загрузки видео
     * @param {HTMLVideoElement} video - видео элемент
     */
    async handleVideoLazyLoad(video) {
        try {
            // Добавляем видео в кеш для управления клавишами
            if (!this.videoElements.includes(video)) {
                this.videoElements.push(video);
            }

            // Показываем индикатор загрузки только при первой загрузке
            const isVideoAlreadyLoaded = video.readyState >= 1; // HAVE_METADATA или выше

            // Проверяем, нужна ли ленивая загрузка
            if (!video.hasAttribute("data-lazy-load") || isVideoAlreadyLoaded) {
                this.playVideoIfNeeded(video);
                return;
            }

            // Обрабатываем ленивую загрузку poster
            const dataPoster = video.getAttribute("data-poster");
            if (dataPoster) {
                video.setAttribute("poster", dataPoster);
                video.removeAttribute("data-poster");
            }

            // Получаем источники из data-src
            const sources = video.querySelectorAll("source[data-src]");
            sources.forEach(source => {
                const dataSrc = source.getAttribute("data-src");
                if (dataSrc) {
                    source.setAttribute("src", dataSrc);
                    source.removeAttribute("data-src");
                }
            });

            // Обновляем src самого video элемента, если есть data-src
            const videoDataSrc = video.getAttribute("data-src");
            if (videoDataSrc) {
                video.setAttribute("src", videoDataSrc);
                video.removeAttribute("data-src");
            }

            // Загружаем видео
            video.load();

            // Ждем готовности метаданных
            await new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    reject(new Error("Таймаут загрузки видео"));
                }, 15000); // 15 секунд таймаут

                video.addEventListener(
                    "loadedmetadata",
                    () => {
                        clearTimeout(timeout);
                        resolve();
                    },
                    { once: true }
                );

                video.addEventListener(
                    "error",
                    () => {
                        clearTimeout(timeout);
                        reject(new Error("Ошибка загрузки видео"));
                    },
                    { once: true }
                );
            });

            // Запускаем воспроизведение если нужно
            this.playVideoIfNeeded(video);
        } catch (error) {
            console.error("Ошибка при загрузке видео:", error);

            // Показываем ошибку пользователю
            this.showVideoError();
        }
    }

    /**
     * Подготовка видео после загрузки (без автовоспроизведения)
     * @param {HTMLVideoElement} video - видео элемент
     */
    playVideoIfNeeded(video) {
        // Автовоспроизведение отключено - видео готово к ручному управлению
        // Видео будет воспроизводиться только при нажатии пользователем на кнопку play
    }

    /**
     * Показ ошибки загрузки видео
     */
    showVideoError() {
        const videoContainer = this.$selector.querySelector(".modal__video");
        if (videoContainer) {
            const errorElement = document.createElement("div");
            errorElement.className = "video-error";
            errorElement.innerHTML = `
                <div class="error-icon">⚠️</div>
                <div class="error-text">Не удалось загрузить видео</div>
                <button class="retry-button" onclick="location.reload()">Попробовать снова</button>
            `;
            videoContainer.appendChild(errorElement);
        }
    }

    /**
     * Очистка всех форм в модальном окне
     */
    async clearModalForms() {
        try {
            // Импортируем улучшенную функцию очистки
            const { default: clearFormFields, clearAllFormsInContainer } = await import("@partials/UI/form/modules/clearFormFields.js");

            // Очищаем все формы в модальном окне
            clearAllFormsInContainer(this.$selector);
        } catch (error) {
            console.error("Ошибка при очистке форм в модальном окне:", error);

            // Fallback - простая очистка
            this.fallbackClearForms();
        }
    }

    /**
     * Fallback очистка форм (если импорт не удался)
     */
    fallbackClearForms() {
        const forms = this.$selector.querySelectorAll(".form");

        forms.forEach(form => {
            const fields = form.querySelectorAll(".field");
            fields.forEach(field => {
                field.value = "";
                field.parentNode?.classList.remove("error");
            });
        });
    }
}
